import { SeverityNumber, logs } from "@opentelemetry/api-logs";
import type { ILogObjMeta } from "tslog";

/**
 * Forwards cal's tslog records to SigNoz.
 *
 * The OTel auto-instrumentation bridges Winston and nothing else, so without this the traces
 * reach SigNoz and the lines that explain them stay on the box as pm2 stdout. That gap is why a
 * silent Google Calendar failure took a session to diagnose.
 *
 * Lives in the logger's `attachedTransports` rather than being attached afterwards: tslog copies
 * that array into every sub-logger at construction, and cal builds ~230 of them at module scope,
 * so a transport attached later reaches the root logger and nothing else.
 */

const SEVERITY_BY_TSLOG_LEVEL: Readonly<Record<number, SeverityNumber>> = {
  0: SeverityNumber.TRACE,
  1: SeverityNumber.TRACE,
  2: SeverityNumber.DEBUG,
  3: SeverityNumber.INFO,
  4: SeverityNumber.WARN,
  5: SeverityNumber.ERROR,
  6: SeverityNumber.FATAL,
};

function describe(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.stack ?? value.message;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

export function otelLogTransport(logObj: ILogObjMeta): void {
  if (process.env.OTEL_ENABLED !== "true") return;

  const meta = logObj._meta;
  // tslog hands the call's arguments back as "0", "1", "2"… alongside _meta.
  const body = Object.keys(logObj)
    .filter((key) => /^\d+$/.test(key))
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => describe((logObj as Record<string, unknown>)[key]))
    .join(" ");

  logs.getLogger(process.env.OTEL_BASE_SERVICE_NAME ?? "calcom").emit({
    timestamp: meta?.date,
    severityNumber: SEVERITY_BY_TSLOG_LEVEL[meta?.logLevelId] ?? SeverityNumber.INFO,
    severityText: meta?.logLevelName,
    body,
    attributes: {
      ...(meta?.name ? { "log.logger": meta.name } : {}),
      ...(meta?.parentNames?.length ? { "log.scope": meta.parentNames.join(".") } : {}),
      ...(meta?.path?.filePathWithLine ? { "code.filepath": meta.path.filePathWithLine } : {}),
    },
  });
}
